
import { create } from 'zustand';
import type {
  AdminStore,
  AdminUser,
  AdminStats,
  AdminFilters,
  AdminUsersResponse,
  AdminStreamsResponse,
  AdminCommentsResponse,
  AdminModerationResponse,
  AdminStatsResponse
} from './types';


import { AdminStatsApiResponse } from '@/lib/shared-types/api/endpoints/admin';
import { isSuccessResponse } from '@/lib/shared-types/api/response';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/stores/auth';
import { ROLE_NAMES, hasPermission } from '@/lib/constants/roles';

const normalizePagination = (
  pagination: { page: number; pageSize: number; total: number; hasMore: boolean } | undefined,
  page: number,
  pageSize: number,
  total: number,
  currentCount: number
) => {
  if (pagination) {
    return pagination;
  }

  const resolvedPageSize = pageSize > 0 ? pageSize : 20;
  return {
    page,
    pageSize: resolvedPageSize,
    total,
    hasMore: (page * resolvedPageSize) < total && currentCount > 0,
  };
};


// API functions
const apiFetchUsers = async (
  filters?: Partial<AdminFilters>,
  page = 1,
  pageSize = 20
): Promise<AdminUsersResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: pageSize.toString(),
    ...(filters?.userStatus && { status: filters.userStatus }),
    ...(filters?.userRole && { role: filters.userRole }),
    ...(filters?.searchQuery && { search: filters.searchQuery }),
  });

  const response = await api.get<AdminUsersResponse>(`api/admin/users?${params.toString()}`);
  if (!isSuccessResponse(response)) {
    throw new Error(response.error?.message || 'Failed to fetch users');
  }

  const data = response.data;
  // Remap backend field names (joinedAt, lastActive) to AdminUser field names (createdAt, lastActivityAt)
  const remappedUsers = (data.users || []).map((u: any): AdminUser => ({
    ...u,
    createdAt: u.createdAt || u.joinedAt,
    lastActivityAt: u.lastActivityAt || u.lastActive,
  }));
  return {
    ...data,
    users: remappedUsers,
    pagination: normalizePagination(data.pagination, page, pageSize, data.total || 0, data.users?.length || 0),
  };
};

const apiFetchStreams = async (
  filters?: Partial<AdminFilters>,
  page = 1,
  pageSize = 20
): Promise<AdminStreamsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: pageSize.toString(),
    ...(filters?.streamStatus && { status: filters.streamStatus }),
    ...(filters?.searchQuery && { search: filters.searchQuery }),
  });

  const response = await api.get<AdminStreamsResponse>(`api/admin/streams?${params.toString()}`);
  if (!isSuccessResponse(response)) {
    throw new Error(response.error?.message || 'Failed to fetch streams');
  }

  const data = response.data;
  return {
    ...data,
    pagination: normalizePagination(data.pagination, page, pageSize, data.total || 0, data.streams?.length || 0),
  };
};

const apiFetchComments = async (
  filters?: Partial<AdminFilters>,
  page = 1,
  pageSize = 20
): Promise<AdminCommentsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: pageSize.toString(),
    ...(filters?.commentStatus && { status: filters.commentStatus }),
    ...(filters?.searchQuery && { search: filters.searchQuery }),
  });

  const response = await api.get<AdminCommentsResponse>(`api/admin/comments?${params.toString()}`);
  if (!isSuccessResponse(response)) {
    throw new Error(response.error?.message || 'Failed to fetch comments');
  }

  const data = response.data;
  return {
    ...data,
    pagination: normalizePagination(data.pagination, page, pageSize, data.total || 0, data.comments?.length || 0),
  };
};

const apiFetchModerationQueue = async (
  filters?: Partial<AdminFilters>,
  page = 1,
  pageSize = 20
): Promise<AdminModerationResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: pageSize.toString(),
    ...(filters?.moderationStatus && { status: filters.moderationStatus }),
  });

  const response = await api.get<AdminModerationResponse>(`api/admin/moderation?${params.toString()}`);
  if (!isSuccessResponse(response)) {
    throw new Error(response.error?.message || 'Failed to fetch moderation queue');
  }

  const data = response.data;
  return {
    ...data,
    pagination: normalizePagination(data.pagination, page, pageSize, data.total || 0, data.items?.length || 0),
  };
};

const apiFetchStats = async (): Promise<AdminStatsResponse> => {
  const response = await api.get<AdminStatsApiResponse>('api/admin/stats');
  if (!isSuccessResponse(response)) {
    throw new Error(response.error?.message || 'Failed to fetch stats');
  }

  const data = response.data;

  // The backend already transforms the data to match frontend AdminStats interface
  // So data.stats should already be in the correct AdminStats format
  const stats: AdminStats = data.stats;

  return { stats };
};


export const useAdminStore = create<AdminStore>((set, get) => ({
  // Initial state
  users: [],
  streams: [],
  comments: [],
  moderationQueue: [],
  stats: null,
  isLoading: false,
  error: null,
  filters: {},
  pagination: {
    users: { page: 1, pageSize: 20, total: 0, hasMore: false },
    streams: { page: 1, pageSize: 20, total: 0, hasMore: false },
    comments: { page: 1, pageSize: 20, total: 0, hasMore: false },
    moderation: { page: 1, pageSize: 20, total: 0, hasMore: false }
  },

  // User management
  fetchUsers: async (filters, page, pageSize) => {
    try {
      set({ isLoading: true, error: null });

      if (filters) {
        set((state) => ({
          filters: { ...state.filters, ...filters }
        }));
      }

      const currentFilters = get().filters;
      const response = await apiFetchUsers(currentFilters, page, pageSize);


      set({
        users: !page || page <= 1 ? response.users : [...get().users, ...response.users],
        pagination: { ...get().pagination, users: response.pagination },
        isLoading: false,
        error: null
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch users';
      set({
        error: errorMessage,
        isLoading: false,
        users: []
      });
      throw error;
    }
  },

  updateUser: async (userId, updates) => {
    try {
      set({ isLoading: true, error: null });

      const updateResponse = await api.put(`api/admin/users/${userId}`, updates);
      if (!updateResponse.success) {
        throw new Error(updateResponse.error?.message || 'Failed to update user');
      }

      // Refresh users list
      await get().fetchUsers();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update user';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  banUser: async (userId, reason) => {
    return get().updateUser(userId, { status: 'banned' });
  },


  unbanUser: async (userId) => {
    return get().updateUser(userId, { status: 'active' });
  },


  deleteUser: async (userId) => {
    try {
      set({ isLoading: true, error: null });

      await api.delete(`api/admin/users/${userId}`);

      await get().fetchUsers();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },


  // Stream management
  fetchStreams: async (filters) => {
    try {
      set({ isLoading: true, error: null });

      if (filters) {
        set((state) => ({
          filters: { ...state.filters, ...filters }
        }));
      }

      const currentFilters = get().filters;
      const response = await apiFetchStreams(currentFilters);


      set({
        streams: response.streams,
        pagination: { ...get().pagination, streams: response.pagination },
        isLoading: false,
        error: null
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch streams';
      set({
        error: errorMessage,
        isLoading: false,
        streams: []
      });
      throw error;
    }
  },

  updateStream: async (streamId, updates) => {
    try {
      set({ isLoading: true, error: null });

      await api.put(`api/admin/streams/${streamId}`, updates);

      await get().fetchStreams();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update stream';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },


  deleteStream: async (streamId) => {
    try {
      set({ isLoading: true, error: null });

      await api.delete(`api/admin/streams/${streamId}`);

      await get().fetchStreams();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete stream';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },


  featureStream: async (streamId, featured) => {
    try {
      set({ isLoading: true, error: null });

      await api.put(`api/admin/streams/${streamId}/feature`, { featured });

      set({ isLoading: false });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update stream feature status';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  // Comment management
  fetchComments: async (filters) => {
    try {
      set({ isLoading: true, error: null });

      if (filters) {
        set((state) => ({
          filters: { ...state.filters, ...filters }
        }));
      }

      const currentFilters = get().filters;
      const response = await apiFetchComments(currentFilters);


      set({
        comments: response.comments,
        pagination: { ...get().pagination, comments: response.pagination },
        isLoading: false,
        error: null
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch comments';
      set({
        error: errorMessage,
        isLoading: false,
        comments: []
      });
      throw error;
    }
  },

  moderateComment: async (commentId, action, reason) => {
    try {
      set({ isLoading: true, error: null });

      if (action === 'delete') {
        await api.delete(`api/admin/comments/${commentId}`);
      } else {
        await api.put(`api/admin/comments/${commentId}/moderate`, { action, reason });
      }

      await get().fetchComments();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to moderate comment';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },


  bulkModerateComments: async (commentIds, action, reason) => {
    try {
      set({ isLoading: true, error: null });

      await api.post('api/admin/moderation/bulk', { itemIds: commentIds, action, reason });

      await get().fetchComments();
      await get().fetchModerationQueue();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to moderate comments';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },


  // Moderation queue
  fetchModerationQueue: async (filters) => {
    try {
      set({ isLoading: true, error: null });

      if (filters) {
        set((state) => ({
          filters: { ...state.filters, ...filters }
        }));
      }

      const currentFilters = get().filters;
      const response = await apiFetchModerationQueue(currentFilters);


      set({
        moderationQueue: response.items,
        pagination: { ...get().pagination, moderation: response.pagination },
        isLoading: false,
        error: null
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch moderation queue';
      set({
        error: errorMessage,
        isLoading: false,
        moderationQueue: []
      });
      throw error;
    }
  },

  resolveModerationItem: async (itemId, resolution) => {
    try {
      set({ isLoading: true, error: null });

      await api.put(`api/admin/moderation/${itemId}/resolve`, { resolution });

      // Update moderation item
      set((state) => ({
        moderationQueue: state.moderationQueue.map(item =>
          item.id === itemId
            ? {
              ...item,
              status: 'resolved' as const,
              resolvedAt: new Date().toISOString(),
              resolution
            }
            : item
        ),
        isLoading: false
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resolve moderation item';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  assignModerationItem: async (itemId, moderatorId) => {
    try {
      await api.put(`api/admin/moderation/${itemId}/assign`, { moderatorId });

      // Update moderation item
      set((state) => ({
        moderationQueue: state.moderationQueue.map(item =>
          item.id === itemId
            ? { ...item, assignedTo: moderatorId }
            : item
        )
      }));

    } catch (error) {
      throw error;
    }
  },

  // Analytics
  fetchStats: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await apiFetchStats();


      set({
        stats: response.stats,
        isLoading: false,
        error: null
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch stats';
      set({
        error: errorMessage,
        isLoading: false,
        stats: null
      });
      throw error;
    }
  },

  exportData: async (type, format) => {
    try {
      set({ isLoading: true, error: null });

      let data: unknown[] = [];
      switch (type) {
        case 'users':
          data = get().users;
          break;
        case 'streams':
          data = get().streams;
          break;
        case 'comments':
          data = get().comments;
          break;
      }

      const content = format === 'json'
        ? JSON.stringify(data, null, 2)
        : convertToCSV(data as Record<string, any>[]);

      const blob = new Blob([content], {
        type: format === 'json' ? 'application/json' : 'text/csv'
      });

      set({ isLoading: false });
      return blob;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export data';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  // System management
  clearCache: async () => {
    try {
      set({ isLoading: true, error: null });

      await api.post('api/admin/system/cache/clear', {});

      set({ isLoading: false });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to clear cache';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  restartServices: async () => {
    try {
      set({ isLoading: true, error: null });

      await api.post('api/admin/system/restart', {});

      set({ isLoading: false });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to restart services';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  updateSystemSettings: async (settings) => {
    try {
      set({ isLoading: true, error: null });

      await api.put('api/admin/system/settings', settings);

      set({ isLoading: false });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update system settings';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  // UI state management
  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: {
        users: { ...state.pagination.users, page: 1 },
        streams: { ...state.pagination.streams, page: 1 },
        comments: { ...state.pagination.comments, page: 1 },
        moderation: { ...state.pagination.moderation, page: 1 }
      }
    }));
  },

  loadMore: async (type) => {
    const state = get();
    const pagination = state.pagination[type];

    if (state.isLoading || !pagination.hasMore) return;

    try {
      set({ isLoading: true });

      const nextPage = pagination.page + 1;

      switch (type) {
        case 'users': {
          const response = await apiFetchUsers(state.filters, nextPage, pagination.pageSize);
          set((currentState) => ({
            users: [...currentState.users, ...response.users],
            pagination: {
              ...currentState.pagination,
              users: response.pagination
            }
          }));
          break;
        }
        case 'streams': {
          const response = await apiFetchStreams(state.filters, nextPage, pagination.pageSize);
          set((currentState) => ({
            streams: [...currentState.streams, ...response.streams],
            pagination: {
              ...currentState.pagination,
              streams: response.pagination
            }
          }));
          break;
        }
        case 'comments': {
          const response = await apiFetchComments(state.filters, nextPage, pagination.pageSize);
          set((currentState) => ({
            comments: [...currentState.comments, ...response.comments],
            pagination: {
              ...currentState.pagination,
              comments: response.pagination
            }
          }));
          break;
        }
        case 'moderation': {
          const response = await apiFetchModerationQueue(state.filters, nextPage, pagination.pageSize);
          set((currentState) => ({
            moderationQueue: [...currentState.moderationQueue, ...response.items],
            pagination: {
              ...currentState.pagination,
              moderation: response.pagination
            }
          }));
          break;
        }
      }

      set({ isLoading: false });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load more data';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  resetData: (type) => {
    if (!type) {
      // Reset all data
      set({
        users: [],
        streams: [],
        comments: [],
        moderationQueue: [],
        stats: null,
        pagination: {
          users: { page: 1, pageSize: 20, total: 0, hasMore: false },
          streams: { page: 1, pageSize: 20, total: 0, hasMore: false },
          comments: { page: 1, pageSize: 20, total: 0, hasMore: false },
          moderation: { page: 1, pageSize: 20, total: 0, hasMore: false }
        }
      });
      return;
    }

    // Reset specific data type
    switch (type) {
      case 'users':
        set({
          users: [],
          pagination: {
            ...get().pagination,
            users: { page: 1, pageSize: 20, total: 0, hasMore: false }
          }
        });
        break;
      case 'streams':
        set({
          streams: [],
          pagination: {
            ...get().pagination,
            streams: { page: 1, pageSize: 20, total: 0, hasMore: false }
          }
        });
        break;
      case 'comments':
        set({
          comments: [],
          pagination: {
            ...get().pagination,
            comments: { page: 1, pageSize: 20, total: 0, hasMore: false }
          }
        });
        break;
      case 'moderation':
        set({
          moderationQueue: [],
          pagination: {
            ...get().pagination,
            moderation: { page: 1, pageSize: 20, total: 0, hasMore: false }
          }
        });
        break;
    }
  },

  // Utility functions
  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  setError: (error) => {
    set({ error, isLoading: false });
  },

  clearError: () => {
    set({ error: null });
  }
}));

// Helper functions
function convertToCSV<T extends Record<string, any>>(data: T[]): string {
  if (data.length === 0) return '';

  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes in CSV
        const stringValue = String(value || '');
        return stringValue.includes(',') || stringValue.includes('"')
          ? `"${stringValue.replace(/"/g, '""')}"`
          : stringValue;
      }).join(',')
    )
  ];

  return csvRows.join('\n');
}

// Selectors for common admin state
export const useAdminUsers = () => useAdminStore((state) => state.users);
export const useAdminStreams = () => useAdminStore((state) => state.streams);
export const useAdminComments = () => useAdminStore((state) => state.comments);
export const useModerationQueue = () => useAdminStore((state) => state.moderationQueue);
export const useAdminStats = () => useAdminStore((state) => state.stats);
export const useAdminLoading = () => useAdminStore((state) => state.isLoading);
export const useAdminError = () => useAdminStore((state) => state.error);

// Helper hooks for admin operations
export const useUserById = (id: number) => {
  const users = useAdminUsers();
  return users.find(user => user.id === id);
};

export const useStreamById = (id: number) => {
  const streams = useAdminStreams();
  return streams.find(stream => stream.id === id);
};

export const useCommentById = (id: number) => {
  const comments = useAdminComments();
  return comments.find(comment => comment.id === id);
};

export const usePendingModerationCount = () => {
  const queue = useModerationQueue();
  return queue.filter(item => item.status === 'pending').length;
};

export const useUrgentModerationItems = () => {
  const queue = useModerationQueue();
};

// Helper hook for admin permissions
export const useAdminPermissions = () => {
  const { user } = useAuthStore.getState();

  if (!user) {
    return {
      canManageUsers: false,
      canModerateContent: false,
      canViewAnalytics: false,
      canManageSystem: false,
      isSuperAdmin: false
    };
  }

  const userRole = user.role?.name || ROLE_NAMES.MEMBER;

  return {
    canManageUsers: hasPermission(userRole, 'CAN_MANAGE_USERS'),
    canModerateContent: hasPermission(userRole, 'CAN_MODERATE_COMMENTS'),
    canViewAnalytics: hasPermission(userRole, 'CAN_VIEW_ANALYTICS'),
    canManageSystem: hasPermission(userRole, 'CAN_MANAGE_SYSTEM'),
    isSuperAdmin: userRole === ROLE_NAMES.ADMIN
  };
};
